import { useCallback, useEffect, useState } from 'react';
import {
  BriefcaseBusiness,
  CreditCard,
  Loader2,
  Plus,
  ShieldAlert,
  UserRound,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FinanceLockGate from '../components/FinanceLockGate';
import {
  createConsultant,
  getConsultants,
  getConsultantLedgerSummary,
} from '../services/consultantLedgerApi';

const emptyConsultantForm = {
  consultantName: '',
  specialization: '',
  phone: '',
  email: '',
  notes: '',
};

function friendlyError(error) {
  return error?.response?.data?.error || error?.message || 'Request failed.';
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

export default function ExternalConsultants() {
  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyConsultantForm);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [consultantRows, ledgerSummary] = await Promise.all([
        getConsultants(),
        getConsultantLedgerSummary().catch(() => null),
      ]);
      setConsultants(Array.isArray(consultantRows) ? consultantRows : []);
      setSummary(ledgerSummary);
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const submitConsultant = async () => {
    if (!form.consultantName.trim()) {
      toast.error('Consultant name is required.');
      return;
    }
    setSaving(true);
    try {
      await createConsultant(form);
      toast.success('External consultant added.');
      setModalOpen(false);
      setForm(emptyConsultantForm);
      await loadData();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FinanceLockGate
      title="Finance Lock enabled. Enter PIN to manage external consultants."
      description="External consultant records can include agreed fees, payments, and balances."
      onAccessGranted={loadData}
    >
      <div className="min-h-screen bg-[#F8FAFC] px-4 py-6 md:px-8">
        <div className="mx-auto max-w-[1420px] space-y-5">
          <section className="overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
            <div className="bg-[linear-gradient(135deg,rgba(37,99,235,0.12),rgba(255,255,255,0.92),rgba(226,232,240,0.55))] px-6 py-6 md:px-8">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">
                    Clinic Consultants
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                    External Consultants
                  </h1>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                    Manage visiting consultants, availability, and consultant payments here.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_22px_rgba(37,99,235,0.22)] transition hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add External Consultant
                </button>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Active Consultants</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{consultants.length}</p>
            </div>
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Consultant Due</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{formatMoney(summary?.totalBalanceRemaining)}</p>
            </div>
            <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 text-amber-800 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5" />
                <div>
                  <p className="text-sm font-semibold">Finance Lock protected</p>
                  <p className="mt-1 text-sm leading-6">Consultant fee and payment amounts remain finance-sensitive.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <BriefcaseBusiness className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Consultant Directory</h2>
                <p className="mt-1 text-sm text-slate-500">External consultants are separate from staff attendance and employee payment slips.</p>
              </div>
            </div>

            {loading ? (
              <div className="mt-6 flex items-center gap-2 rounded-[22px] border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                Loading consultants...
              </div>
            ) : consultants.length === 0 ? (
              <div className="mt-6 rounded-[24px] border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                <UserRound className="mx-auto h-8 w-8 text-blue-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">No external consultants added yet.</p>
                <p className="mt-1 text-sm text-slate-500">Add visiting consultants here without mixing them into staff attendance.</p>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {consultants.map((consultant) => (
                  <div key={consultant.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{consultant.consultantName}</p>
                        <p className="mt-1 text-sm text-slate-500">{consultant.specialization || 'General visiting consultant'}</p>
                      </div>
                      <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                        {consultant.status || 'active'}
                      </span>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                      <p>Mobile: <span className="font-semibold text-slate-800">{consultant.phone || '-'}</span></p>
                      <p>Email: <span className="font-semibold text-slate-800">{consultant.email || '-'}</span></p>
                    </div>
                    {consultant.notes ? <p className="mt-3 text-sm text-slate-500">{consultant.notes}</p> : null}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {['Availability', 'Payment Records', 'Case Links'].map((title) => (
              <div key={title} className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <h3 className="mt-3 font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  This section will connect to the existing Consultant Fees Ledger as records grow.
                </p>
              </div>
            ))}
          </section>
        </div>

        {modalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl rounded-[26px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
              <div className="border-b border-slate-100 px-6 py-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">External Consultants</p>
                <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-900">Add External Consultant</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
                {[
                  ['consultantName', 'Consultant Name *'],
                  ['specialization', 'Specialization'],
                  ['phone', 'Mobile Number'],
                  ['email', 'Email'],
                ].map(([field, label]) => (
                  <label key={field} className="block">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
                    <input
                      value={form[field]}
                      onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                    />
                  </label>
                ))}
                <label className="block md:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Notes</span>
                  <textarea
                    value={form.notes}
                    onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    className="mt-2 min-h-[110px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                <button type="button" onClick={() => setModalOpen(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
                <button type="button" onClick={submitConsultant} disabled={saving} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                  {saving ? 'Saving...' : 'Add Consultant'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </FinanceLockGate>
  );
}
